import useIsDarkMode from '@/hooks/useIsDarkMode';
import { Category } from '@/types/category';
import { resolveCategoryImageByName } from '@/utils/category';
import { Box, VStack, chakra, useBreakpointValue } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

type Props = {
  category?: Category | null;
  imageUrl?: string | null;
  children?: JSX.Element;
  fadeIn?: boolean;
  isArchived?: boolean;
  noCategoryGradient?: boolean;
  dynamicHeight?: string; // New prop for dynamic height
  onImageLoad?: () => void; // Callback when background image loads
  fromCategorySpotlight?: boolean;
};

const MotionDiv = chakra(motion.div);

const HeroImage = ({
  category,
  imageUrl,
  fadeIn = false,
  children,
  isArchived,
  noCategoryGradient = false,
  dynamicHeight,
  onImageLoad,
  fromCategorySpotlight = false,
}: Props) => {
  const isDarkMode = useIsDarkMode();
  const [bgImageWidth, setBgImageWidth] = useState(0);
  const [bgImageHeight, setBgImageHeight] = useState(0);
  const [hasImageLoaded, setHasImageLoaded] = useState(false);
  const onImageLoadRef = useRef(onImageLoad);

  // Update the ref when onImageLoad changes
  useEffect(() => {
    onImageLoadRef.current = onImageLoad;
  }, [onImageLoad]);

  useEffect(() => {
    setBgImageWidth(window.innerWidth);
    setBgImageHeight(Math.ceil(window.innerHeight * 0.7));
  }, []);

  const backgroundImageUrl =
    bgImageWidth && bgImageHeight && imageUrl
      ? resolveCategoryImageByName(imageUrl) +
        (imageUrl.includes('images.unsplash.com')
          ? `&w=${bgImageWidth}&h=${bgImageHeight}&fit=crop&crop=faces%2Cedges&dpr=2&q=37`
          : '')
      : '';

  // Effect to preload the background image and call onImageLoad when ready
  useEffect(() => {
    if (!backgroundImageUrl) {
      // If no background image, call onImageLoad immediately
      if (onImageLoadRef.current) {
        onImageLoadRef.current();
      }
      return;
    }

    // Reset loading state for new image
    setHasImageLoaded(false);

    let isCancelled = false; // Flag to track if this effect was cancelled

    const img = new Image();
    img.onload = () => {
      if (isCancelled) return;

      setHasImageLoaded(true);
      if (onImageLoadRef.current) {
        onImageLoadRef.current();
      }
    };

    img.onerror = () => {
      if (isCancelled) return;

      // Even if image fails to load, we should show the content
      setHasImageLoaded(true);
      if (onImageLoadRef.current) {
        onImageLoadRef.current();
      }
    };

    // Start loading the image
    img.src = backgroundImageUrl;

    // Cleanup function to prevent memory leaks and race conditions
    return () => {
      isCancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [backgroundImageUrl]);
  return (
    <MotionDiv
      initial={{ opacity: fadeIn && category ? 0 : 1 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      width="full"
      position="relative"
      zIndex={5}
    >
      <Box
        sx={{
          '@media print': {
            backgroundImage: 'unset',
            _before: {
              bgGradient: 'unset',
            },
            _after: {
              bgGradient: 'unset',
            },
          },
        }}
        position="absolute"
        zIndex={-1}
        top={0}
        right={0}
        left={0}
        width="full"
        height={
          dynamicHeight
            ? dynamicHeight
            : noCategoryGradient
              ? { base: '80vh', md: '80vh', lg: '80vh', '3xl': '75vh' }
              : '75vh'
        }
        backgroundImage={`url(${backgroundImageUrl})`}
        backgroundSize="cover"
        backgroundPosition="center center"
        backgroundRepeat="no-repeat"
        _before={{
          content: '""',
          display: 'block',
          width: 'full',
          height: 'full',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bgGradient: `linear-gradient(180deg, transparent, ${isDarkMode ? 'blue.1000' : 'gray.50'} ${noCategoryGradient ? '95%' : '80%'})`,

          zIndex: 0,
        }}
        _after={
          noCategoryGradient
            ? {}
            : {
                content: "''",
                width: 'full',
                height: 'full',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bgGradient: `radial-gradient(50% 75.96% at 17.36% 20%, ${category?.color}, rgba(97, 154, 250, 0.00) 100%)`,
                zIndex: 0,
              }
        }
        filter={isArchived ? 'grayscale(100%)' : 'none'}
      />

      <VStack
        position="relative"
        zIndex={1}
        alignItems="start"
        width="full"
        height="full"
        paddingX={useBreakpointValue({
          base: 4,
          md: fromCategorySpotlight ? 6 : 10,
          lg: fromCategorySpotlight ? 0 : 16,
        })}
        paddingY={useBreakpointValue({ base: 10, lg: 0 })}
      >
        {children}
      </VStack>
    </MotionDiv>
  );
};

export default HeroImage;
